import { CircleAlert, CircleCheck, CircleHelp, CircleMinus, CircleX, Droplet, Hourglass, Truck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
    getLeakEntityStatusDefinition,
    getPoiEntityStatusDefinition,
    type LeakEntityStatus,
    type PoiEntityStatus,
} from '@/components/map/utils/entityStatus';
import type { VanFeatureProperties } from '@/components/map/utils/vanFeatureProperties';
import { formatVanState } from '@/components/map/utils/vanFeatureProperties';

/**
 * One resolved status pill: the design-pack treatment is the status's pale disc colour as
 * the background with the saturated status colour for the text and icon. Neutral pills
 * (Awaiting Assessment, Unknown van) instead sit on the themed background with a border,
 * so they behave in dark mode rather than pinning a white chip.
 */
export interface StatusPillPresentation {
    label: string;
    icon: LucideIcon;
    /** Fixed hex colours from the marker design pack; unused when `neutral`. */
    background: string;
    color: string;
    neutral?: boolean;
}

// POI pill icons mirror the marker glyphs (circled outcome symbols).
const POI_PILL_ICONS: Record<PoiEntityStatus, LucideIcon> = {
    awaiting_assessment: Hourglass,
    leak_found: CircleCheck,
    no_leak_found: CircleX,
    inconclusive: CircleHelp,
    unable_to_check: CircleAlert,
    unclassified: CircleMinus,
};

/**
 * Statuses come off the wire as strings; anything the registry does not recognise —
 * including legacy pre-split values — presents as Unclassified, the registry's own
 * "needs data attention" rule, rather than being silently mapped to a plausible status.
 */
export function getPoiStatusPillPresentation(status: string | null | undefined): StatusPillPresentation {
    const def = getPoiEntityStatusDefinition(status as PoiEntityStatus);
    if (def.status === 'awaiting_assessment') {
        return { label: def.label, icon: POI_PILL_ICONS[def.status], background: '', color: '', neutral: true };
    }
    return {
        label: def.label,
        icon: POI_PILL_ICONS[def.status],
        background: def.discColor,
        color: def.glyphColor,
    };
}

export function getLeakStatusPillPresentation(status: string | null | undefined): StatusPillPresentation {
    const def = getLeakEntityStatusDefinition(status as LeakEntityStatus);
    // Two design-pack exceptions to the disc/glyph pairing: Already Marked Up inverts to a
    // solid saturated pill, and Leak Found's marker glyph is near-white — unreadable as
    // text — so its pill text takes the family's saturated blue instead.
    if (def.status === 'already_marked_up') {
        return { label: def.label, icon: Droplet, background: def.haloColor, color: '#FFFFFF' };
    }
    return {
        label: def.label,
        icon: Droplet,
        background: def.discColor,
        color: def.status === 'leak_found' ? def.haloColor : def.glyphColor,
    };
}

// Van states have no marker colour registry (the van marker deliberately encodes no
// state), so the pill palette is defined here using the same colour families as the
// POI/leak packs: red danger, yellow caution, purple parked, blue active, green moving.
const VAN_PILL_COLOURS: Record<Exclude<VanFeatureProperties['state'], 'unknown'>, [string, string]> = {
    offline: ['#FDBDBE', '#E5484D'],
    stale: ['#FBE577', '#E2A336'],
    parked: ['#E0C4F4', '#8E4EC6'],
    stationary: ['#ACD8FC', '#0090FF'],
    moving: ['#ACDEC8', '#29A383'],
};

export function getVanStatePillPresentation(state: VanFeatureProperties['state']): StatusPillPresentation {
    const label = formatVanState(state);
    if (state === 'unknown') {
        return { label, icon: Truck, background: '', color: '', neutral: true };
    }
    const [background, color] = VAN_PILL_COLOURS[state];
    return { label, icon: Truck, background, color };
}
